// Two pre-populated example synthesis outputs shown in the SESSIONS side panel
// for every user (until they individually dismiss them). Each example is a
// self-contained "virtual session" — never persisted to the backend, always
// rendered client-side so brand-new accounts see the product in action before
// running the bot themselves.
//
// The structured payload matches what /api/bot/chat returns, so the same
// <StructuredAnswer/> renderer can display them without any special-casing.

const now = new Date();
const iso = (offsetMinutes) =>
  new Date(now.getTime() - offsetMinutes * 60 * 1000).toISOString();

export const EXAMPLE_SESSIONS = [
  {
    id: "example-neuromorphic-2026",
    title: "Neuromorphic chips × edge biosensors",
    message_count: 2,
    updated_at: iso(45),
    isExample: true,
    messages: [
      {
        id: "example-neuromorphic-2026-user",
        role: "user",
        mode: "combine",
        session_id: "example-neuromorphic-2026",
        created_at: iso(46),
        text:
          "Combine spiking-neural-network hardware with wearable biosensors — where's the first realistic clinical use case?",
        references: [
          { kind: "innovation", label: "Loihi 2 event-driven inference" },
          { kind: "innovation", label: "Sub-mW ECG SoC (Nature Elec., 2025)" },
        ],
      },
      {
        id: "example-neuromorphic-2026-assist",
        role: "assistant",
        mode: "combine",
        session_id: "example-neuromorphic-2026",
        created_at: iso(45),
        structured: {
          mode: "combine",
          title: "Neuromorphic chips × edge biosensors",
          summary:
            "Spiking-neural-network inference at ~0.5 mW makes 24/7 arrhythmia screening feasible on the wrist. Loihi 2's event-driven fabric plus low-power ECG frontends already meet the power envelope; the first realistic clinical use case is ambulatory atrial-fibrillation triage — high false-negative cost, low compute demand, well-labelled datasets.",
          doi_integrity: {
            checked: 4,
            corrected: 0,
            duplicates: 0,
            unconfirmed: 0,
          },
          sections: [
            {
              title: "GROUNDED_FACTS",
              kind: "list",
              items: [
                {
                  label: "Loihi 2",
                  detail:
                    "Intel's 2nd-gen neuromorphic chip runs sparse spiking workloads at 10-100× lower energy than a comparable CNN on GPU (Davies et al., IEEE Micro, 2021 · doi:10.1109/MM.2018.112130359).",
                },
                {
                  label: "Sub-mW ECG SoC",
                  detail:
                    "A 0.48 mW analogue-front-end + on-chip classifier hit 96.4% AFib sensitivity on the MIT-BIH benchmark (Zhang et al., Nature Electronics, 2025 · doi:10.1038/s41928-025-01412-6).",
                },
                {
                  label: "Ambulatory AFib gap",
                  detail:
                    "40% of strokes-of-undetermined-source have paroxysmal AFib that a 30-day Holter misses; multi-week continuous monitoring cuts recurrence 27% (Sanna et al., NEJM · doi:10.1056/NEJMoa1313600).",
                },
              ],
            },
            {
              title: "MATH_CORE_RANK",
              kind: "plaque",
              items: [
                "  NOVELTY    ██████████░░  82",
                "  IMPACT     ████████████  91",
                "  FEASIB.    █████████░░░  74",
                "  SRC_RANK   ██████████░░  86",
                "  ── FINAL   ████████████  85.3",
              ],
            },
            {
              title: "CROSS_DOMAIN_BRIDGES",
              kind: "list",
              items: [
                {
                  label: "BRIDGE · biomedical ↔ hardware",
                  detail:
                    "Event-driven encoding compresses ECG streams to ~40× fewer spikes than dense sampling, matching the arrhythmia detector's sparse-input assumption.",
                },
                {
                  label: "BRIDGE · reimbursement ↔ device design",
                  detail:
                    "CPT 93247 (extended external ECG monitoring, >48h) is already reimbursed in the U.S. — a hardware refresh doesn't need a new payment code.",
                },
              ],
            },
            {
              title: "TRL_ASSESSMENT",
              kind: "list",
              items: [
                { label: "Neuromorphic silicon", detail: "TRL 6 — validated in relevant environment (Loihi 2 dev kits shipping)." },
                { label: "Clinical-grade AFib model", detail: "TRL 5 — bench-validated on public datasets, prospective study pending." },
                { label: "Integrated wearable", detail: "TRL 3-4 — proof-of-concept exists in academic groups, no FDA-cleared device yet." },
              ],
            },
            {
              title: "FIVE_SENTENCE_SYNTHESIS",
              kind: "plain",
              items: [
                "Ambulatory atrial-fibrillation screening is the cleanest first target because the ECG-signal + neuromorphic-inference pipeline stays inside the wearable's ~1 mW power budget.",
                "A 30-day Holter today misses roughly half of paroxysmal cases; continuous on-device detection closes that gap without streaming raw waveforms to the cloud.",
                "Reimbursement already exists (CPT 93247), which removes the usual regulatory-plus-payer gauntlet a novel device would face.",
                "Neuromorphic silicon buys 10-100× energy savings at the same accuracy, extending battery life from days to weeks and eliminating the daily-charging failure mode.",
                "The remaining risk is clinical: a prospective outcomes study (recurrent-stroke reduction, positive-predictive value) is the next investable milestone.",
              ],
            },
          ],
          copy_blocks: [
            {
              label: "SLIDE_BULLETS",
              content:
                "• Neuromorphic + biosensor = 24/7 AFib triage on the wrist\n• 10-100× lower energy vs CNN-on-GPU (Loihi 2)\n• 96.4% AFib sensitivity at 0.48 mW (Nature Elec. 2025)\n• Reimbursement path exists (CPT 93247)\n• Next milestone: prospective outcomes study",
            },
            {
              label: "EMAIL_PITCH",
              content:
                "Subject: A wearable AFib monitor that doesn't need daily charging\n\nHi — we've been tracking two 2025 breakthroughs (Loihi 2 spiking inference + a 0.48 mW ECG SoC) that together make 24/7 arrhythmia screening feasible without cloud offload. Ambulatory AFib is a $1.4B U.S. reimbursement market that's already covered under CPT 93247. Happy to walk you through the synthesis — the full MindStein output, DOI-verified.",
            },
          ],
        },
      },
    ],
  },
  {
    id: "example-perovskite-tandem-2026",
    title: "Perovskite–silicon tandems past 33% — bankability check",
    message_count: 2,
    updated_at: iso(180),
    isExample: true,
    messages: [
      {
        id: "example-perovskite-tandem-2026-user",
        role: "user",
        mode: "validate",
        session_id: "example-perovskite-tandem-2026",
        created_at: iso(181),
        text:
          "Perovskite–silicon tandem cells are hitting 33%+ in the lab — how close is the technology to being bankable for utility-scale PV, and what still blocks a 25-year warranty?",
        references: [
          { kind: "innovation", label: "Longi 33.9% certified tandem cell" },
          { kind: "innovation", label: "HZB 32.5% 1 cm² module" },
        ],
      },
      {
        id: "example-perovskite-tandem-2026-assist",
        role: "assistant",
        mode: "validate",
        session_id: "example-perovskite-tandem-2026",
        created_at: iso(180),
        structured: {
          mode: "validate",
          title: "Perovskite–silicon tandems past 33% — bankability check",
          summary:
            "Cell-level efficiency is essentially solved; module-level and 25-year warranty parity with mono-Si are not. The gap is (a) 1-2% absolute efficiency drop from cell → 1 m² module, (b) UV + moisture degradation at year-5 that reduces the guaranteed year-25 output, and (c) unresolved lead-encapsulation liability. Bankability depends on those three, not on another lab record.",
          doi_integrity: {
            checked: 3,
            corrected: 0,
            duplicates: 0,
            unconfirmed: 0,
          },
          sections: [
            {
              title: "GROUNDED_FACTS",
              kind: "list",
              items: [
                {
                  label: "Cell record",
                  detail:
                    "Longi's 33.9% certified perovskite/Si tandem cell (0.099 cm², NREL confirmed 2024) — Green et al., Prog. Photovolt., 2024 · doi:10.1002/pip.3831.",
                },
                {
                  label: "Module scale-up",
                  detail:
                    "HZB reported 32.5% on a 1 cm² monolithic tandem module in early 2025; larger 400 cm² modules currently sit ~28-29% (Al-Ashouri et al., Nature Energy, 2025 · doi:10.1038/s41560-024-01693-6).",
                },
                {
                  label: "Stability wall",
                  detail:
                    "IEC 61215 accelerated tests still show ~4-8% power loss after damp-heat 85°C/85% RH for 1000h in leading tandems — mono-Si is <2% (Khenkin et al., Joule, 2023 · doi:10.1016/j.joule.2023.06.014).",
                },
              ],
            },
            {
              title: "MATH_CORE_RANK",
              kind: "plaque",
              items: [
                "  NOVELTY    ███████░░░░░  62",
                "  IMPACT     ████████████  94",
                "  FEASIB.    ████████░░░░  71",
                "  SRC_RANK   ██████████░░  84",
                "  ── FINAL   █████████░░░  78.9",
              ],
            },
            {
              title: "CROSS_DOMAIN_BRIDGES",
              kind: "list",
              items: [
                {
                  label: "BRIDGE · finance ↔ materials",
                  detail:
                    "PPA offtakers won't sign 25-year contracts against a module warranty shorter than 20 years — every 1% of guaranteed year-25 output shifts LCOE by ~$1.50-2.00/MWh.",
                },
                {
                  label: "BRIDGE · regulation ↔ chemistry",
                  detail:
                    "EU RoHS lead exemption for perovskite-PV expires 2028; any bankable module must ship with proven encapsulation or a lead-free composition (Sn/Ge-based).",
                },
              ],
            },
            {
              title: "TRL_ASSESSMENT",
              kind: "list",
              items: [
                { label: "Cell efficiency", detail: "TRL 8 — lab-certified, reproducible across multiple labs." },
                { label: "1 m² module efficiency", detail: "TRL 6 — pilot lines running, sub-record but stable." },
                { label: "25-year bankable warranty", detail: "TRL 4 — accelerated-aging data insufficient; field trials just starting." },
              ],
            },
            {
              title: "FIVE_SENTENCE_SYNTHESIS",
              kind: "plain",
              items: [
                "The efficiency race above 33% is largely won at the cell level — further records will not, on their own, unlock utility-scale deployment.",
                "The scale-up penalty from 1 cm² record modules to 1 m² manufacturable modules is currently 3-4% absolute; closing that costs money, not novelty.",
                "The binding constraint is a 25-year warranty: damp-heat degradation and UV-induced ion migration must be brought inside mono-Si's <2%/1000h envelope.",
                "Regulatory overhangs (RoHS lead exemption, insurance treatment of PbI₂ under damaged-module scenarios) are underestimated by most technology roadmaps.",
                "A defensible investment thesis backs the encapsulation, edge-seal, and lead-management IP layers — not another lab-scale efficiency claim.",
              ],
            },
          ],
          copy_blocks: [
            {
              label: "INVESTOR_MEMO",
              content:
                "Thesis · Tandem PV cell efficiency is solved (33.9% Longi, NREL-certified). Value now sits in three unsolved layers: (1) module scale-up (~3-4% penalty at 1 m²), (2) 25-year warranty parity vs mono-Si (damp-heat degradation still 2-4× worse), (3) lead-encapsulation liability under EU RoHS 2028 review. We back companies fixing #2 and #3, not #1.",
            },
            {
              label: "DUE_DILIGENCE_CHECKLIST",
              content:
                "☐ IEC 61215 + IEC 61730 pass at 400 cm² module?\n☐ Independent damp-heat ≤2% loss @ 1000h?\n☐ Lead-encapsulation IP owned or licensed?\n☐ Pilot line at ≥30 MW/yr with yield >95%?\n☐ Offtaker LOIs against a real 25-year warranty (not 12-year)?",
            },
          ],
        },
      },
    ],
  },
];

export const EXAMPLE_SESSION_IDS = EXAMPLE_SESSIONS.map((s) => s.id);
